import { graphql } from '../../gql';

export const HEALTH_CHECK_FRAGMENTS = {
  EntityV2: graphql(`
    query EntityV2HealthCheck($req: EntityV2Req) {
      entityV2(req: $req) {
        entity {
          name
          description
          founded
          headquarters
        }
        competitors {
          id
          shortName
          longName
          closeness
        }
      }
    }
  `),
  CompositionV3: graphql(`
    query CompositionV3HealthCheck(
      $entities: [CompositionV3Ent!]!
      $filters: CompositionV3Filters!
      $corrections: CompositionV3Corrections!
      $role_taxonomy: Int!
      $skill_taxonomy: Int!
      $breakdown_options: CompositionV3BreakdownOptions!
    ) {
      composition_v3(
        req: {
          entities: $entities
          filters: $filters
          corrections: $corrections
          role_taxonomy: $role_taxonomy
          skill_taxonomy: $skill_taxonomy
          breakdown_options: $breakdown_options
        }
      ) {
        entities {
          id
          short_name
          long_name
          metrics {
            headcount {
              id
              val
            }
            growth_rate {
              id
              val
            }
            hiring_rate {
              id
              val
            }
            attrition_rate {
              id
              val
            }
          }
        }
      }
    }
  `),
  TransitionsV2: graphql(`
    query TransitionsV2HealthCheck($req: TransitionsReqV2) {
      transitionsV2(req: $req) {
        entities {
          metadata {
            id
            short_name
            long_name
            type
          }
          metrics {
            flows {
              inflows {
                id
                short_name
                long_name
                count
                share
              }
              outflows {
                id
                short_name
                long_name
                count
                share
              }
              inflows_remaining {
                count
                share
              }
              outflows_remaining {
                count
                share
              }
            }
          }
        }
      }
    }
  `),
  Sentiment: graphql(`
    query SentimentHealthCheck(
      $filters: SentimentV2Filters
      $dim1: Dimension1
    ) {
      sentiment(filters: $filters, dim1: $dim1) {
        metadata {
          id
          shortName
          longName
          metadata_type
        }
        result {
          scores {
            overall {
              count
              value
              month_id
              weight
            }
            business_outlook {
              count
              value
              month_id
              weight
            }
            career_growth {
              count
              value
              month_id
              weight
            }
          }
        }
        months_refs {
          id
          shortName
          longName
        }
      }
    }
  `),
  Auth: graphql(`
    query AuthHealthCheck($ids: [String]) {
      users(ids: $ids) {
        id
        name
        username
        email
        role
        client_name
        live
        active
        tabs
        company_lists
        linkup_postings
        unified_postings
        num_seats
        custom_taxonomy
        metadata {
          key
          value
        }
      }
    }
  `),
  SavedSets: graphql(`
    query SavedSetsHealthCheck {
      presets {
        id
        name
        view
        default
        shared
        filters
      }
    }
  `),
  SharedSelectionList: graphql(`
    query SharedSelectionListHealthCheck {
      selectionList {
        region {
          id
          shortName
          longName
          label: longName
        }
        country {
          id
          shortName
          longName
          label: longName
          region
        }
        state {
          id
          shortName
          longName
          label: longName
          region
          country
        }
        metro_area: msa {
          id
          shortName
          longName
          label: longName
          region
          country
          state
        }
        job_category {
          id
          shortName
          longName
          label: longName
          topCleanedTitles
          topRawTitles
        }
        role_k150 {
          id
          shortName
          longName
          label: longName
          topCleanedTitles
          topRawTitles
          job_category: roleK7
          role_k50: roleK50
        }
        seniority {
          id
          shortName
          longName
          label: longName
        }
      }
    }
  `),
  MonthWeekIndex: graphql(`
    query MonthWeekIndexHealthCheck {
      index {
        startMonthIndex
        endMonthIndex
        startWeekIndex
        endWeekIndex
        maxWeekIndex
        endMonth
        maxWeek
      }
    }
  `),
  CompositionSummaryData: graphql(`
    query CompositionSummaryV3Data(
      $entities: [CompositionV3Ent!]!
      $filters: CompositionV3Filters!
      $corrections: CompositionV3Corrections!
      $role_taxonomy: Int!
      $skill_taxonomy: Int!
      $breakdown_options: CompositionV3BreakdownOptions!
    ) {
      composition_v3(
        req: {
          entities: $entities
          filters: $filters
          corrections: $corrections
          role_taxonomy: $role_taxonomy
          skill_taxonomy: $skill_taxonomy
          breakdown_options: $breakdown_options
        }
      ) {
        entities {
          id
          short_name
          long_name
          metrics {
            headcount {
              id
              val
            }
            growth_rate {
              id
              val
            }
            hiring_rate {
              id
              val
            }
            attrition_rate {
              id
              val
            }
          }
        }
      }
    }
  `),
  Postings: graphql(`
    query EntityPostingsHealthCheck(
      $filters: PostingFilters
      $dim1: Dimension1
    ) {
      posting(filters: $filters, dim1: $dim1, dim2: date) {
        metadata {
          id
          shortName
          longName
          __typename
        }
        category {
          metadata {
            id
            shortName
            longName
            __typename
          }
          metrics {
            salary
            time_to_fill
            active
            new
            removed
            expected_hires
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  PostingsRoles: graphql(`
    query EntityPostingsRolesHealthCheck(
      $filters: PostingFilters
      $dim1: Dimension1
    ) {
      posting(filters: $filters, dim1: $dim1, dim2: top) {
        metadata {
          id
          shortName
          longName
          __typename
        }
        category {
          metadata {
            id
            shortName
            longName
            __typename
          }
          metrics {
            count
            share
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  CompanyIndustrySelectionList: graphql(`
    query CompanyIndustySelectionListHealthCheck {
      selectionList {
        company {
          id
          shortName
          rank
          ticker
          longName
          label: longName
          industry: industryId
          ricsK50Id
          rics_k50: ricsK50Id
          ricsK400Id
          rics_k400: ricsK400Id
          sedol
          isin
          rcid
        }
        industry {
          id
          shortName
          longName
          label: longName
        }
        rics_k10 {
          id
          shortName
          label: longName
          longName
        }
        rics_k50 {
          id
          shortName
          longName
          label: longName
          rics_k10
        }
        rics_k400 {
          id
          shortName
          longName
          rics_k10
          rics_k50
        }
      }
    }
  `),
  GeographySummaryMapData: graphql(`
    query GeographySummaryMapData($query: MapCoordsReq!) {
      mapCoords(req: $query) {
        region {
          id
          name
          lat
          lon
        }
        country {
          id
          name
          lat
          lon
        }
        msa {
          id
          name
          lat
          lon
        }
      }
    }
  `),
  Compensation: graphql(`
    query GetCompensationDataHealthCheck(
      $filters: CompensationFilters
      $dim1: Dimension1
      $dim2: CompensationDimension
    ) {
      compensation(filters: $filters, dim1: $dim1, dim2: $dim2) {
        dimension
        metadata {
          id
          shortName
          __typename
        }
        category {
          metadata {
            id
            shortName
            __typename
          }
          metrics {
            count
            share
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  Screener: graphql(`
    query GetScreenerDataHealthCheck($filters: ScreenerReq!) {
      screener(filters: $filters) {
        pagination {
          total_results
          __typename
        }
        entities {
          short_name
          long_name
          entity_id
          headcount
          inflow
          outflow
          hiring
          attrition
          growth
          growth_yoy
          salary
          tenure
          entity_info {
            company_info {
              description
              email
              founder_api
              founder_black
              founder_female
              founder_hispanic
              founder_male
              founder_multiple
              founder_native
              founder_white
              funding_stage
              hq_country
              hq_metro_area
              hq_region
              hq_state
              is_public
              last_funding_amount
              last_funding_year
              linkedin_url
              phone_number
              rcid
              rics_k10
              rics_k50
              rics_k400
              total_funding_amount
              ultimate_parent_name
              ultimate_parent_rcid
              valuation
              website
              year_founded
              __typename
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  UserSearchHealthCheck: graphql(`
    query UserSearchHealthCheck($input: String!) {
      userSearch(input: $input) {
        id
        name
        email
        client_name
        active
        live
        __typename
      }
    }
  `),
  ReportConfigHealthCheck: graphql(`
    query ReportConfigHealthCheck {
      reports_validation {
        report_validation {
          id
          report_type
          label
          description
          sample_link
          category
          main_entity {
            selection_list
            limit
            required
            label
            description
            __typename
          }
          comparison_entities {
            selection_list
            limit
            required
            label
            description
            __typename
          }
          filters {
            selection_list
            limit
            required
            label
            description
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  TalentDiscoveryV3: graphql(`
    query TalentDiscoveryV3HealthCheck($query: TalentDiscoveryReqV3) {
      talent_discovery_search_v3(query: $query) {
        columns
        users {
          RCID
          first_name
          last_name
          current_company
          current_title
          email
          user_location
          salary_total
          __typename
        }
        pagination {
          tot_pages_num
          tot_users_num
          __typename
        }
        __typename
      }
    }
  `),
} as const;
